import React from "react";
import { Maximize, Minimize, Camera, Home } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

interface ViewerControlsProps {
  fullScreen: boolean;
  onToggleFullScreen: () => void;
  onTakeScreenshot: () => void;
  onResetCamera: () => void;
  isRendering: boolean;
  isMobile?: boolean;
}

export const ViewerControls: React.FC<ViewerControlsProps> = ({
  fullScreen,
  onToggleFullScreen,
  onTakeScreenshot,
  onResetCamera,
  isRendering,
  isMobile = false,
}) => {
  return (
    <div
      className={cn(
        "absolute z-20",
        isMobile ? "bottom-6 right-6" : "top-6 right-6"
      )}
    >
      <div className="flex flex-col gap-3">
        {/* Fullscreen Toggle with Label - Hidden on Mobile */}
        {!isMobile && (
          <div className="group/control relative">
            <Button
              variant="secondary"
              size="icon"
              className="w-12 h-12 bg-white/95 backdrop-blur-md border-2 border-gray-200/50 hover:bg-white hover:border-black hover:shadow-xl transition-all duration-500 group"
              onClick={onToggleFullScreen}
            >
              {fullScreen ? (
                <Minimize
                  size={18}
                  className="stroke-[1.5px] text-gray-600 group-hover:text-black transition-all duration-300"
                />
              ) : (
                <Maximize
                  size={18}
                  className="stroke-[1.5px] text-gray-600 group-hover:text-black transition-all duration-300"
                />
              )}
            </Button>
            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover/control:opacity-100 transition-all duration-300 pointer-events-none">
              <div
                className="bg-black text-white text-xs px-3 py-2 whitespace-nowrap"
                style={{ fontWeight: "300" }}
              >
                {fullScreen ? "Exit Fullscreen" : "Fullscreen"}
              </div>
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-black border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
            </div>
          </div>
        )}

        {/* Screenshot Button with Label - Hidden on Mobile */}
        {!isMobile && (
          <div className="group/control relative">
            <Button
              variant="secondary"
              size="icon"
              className="w-12 h-12 bg-white/95 backdrop-blur-md border-2 border-gray-200/50 hover:bg-white hover:border-black hover:shadow-xl transition-all duration-500 group"
              onClick={onTakeScreenshot}
              disabled={isRendering}
            >
              <Camera
                size={18}
                className={cn(
                  "stroke-[1.5px] text-gray-600 group-hover:text-black transition-all duration-300",
                  isRendering && "animate-pulse"
                )}
              />
            </Button>
            {/* Tooltip */}
            <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover/control:opacity-100 transition-all duration-300 pointer-events-none">
              <div
                className="bg-black text-white text-xs px-3 py-2 whitespace-nowrap"
                style={{ fontWeight: "300" }}
              >
                {isRendering ? "Taking Screenshot..." : "Take Screenshot"}
              </div>
              <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-black border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
            </div>
          </div>
        )}

        {/* Reset Camera Button with Label */}
        <div className="group/control relative">
          <Button
            variant="secondary"
            size="icon"
            className="w-12 h-12 bg-white/95 backdrop-blur-md border-2 border-gray-200/50 hover:bg-white hover:border-black hover:shadow-xl transition-all duration-500 group"
            onClick={onResetCamera}
          >
            <Home
              size={18}
              className="stroke-[1.5px] text-gray-600 group-hover:text-black transition-all duration-300"
            />
          </Button>
          {/* Tooltip */}
          <div className="absolute right-full mr-3 top-1/2 transform -translate-y-1/2 opacity-0 group-hover/control:opacity-100 transition-all duration-300 pointer-events-none">
            <div
              className="bg-black text-white text-xs px-3 py-2 whitespace-nowrap"
              style={{ fontWeight: "300" }}
            >
              Reset Camera
            </div>
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-black border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent"></div>
          </div>
        </div>
      </div>
    </div>
  );
};
